<template>
  <div class="tool-strengths__container">
    <template v-if="ui.answers.loading">
      <div class="d-flex flex-column justify-content-center align-items-center my-3">
        <loading-spinner size="2x"
                         class="m-4"></loading-spinner>
        <p>Saving your answers, please wait...</p>
      </div>
    </template>
    <template v-else>
      <div class="tool-strengths">
        <div class="tool-strengths__background"></div>
        <template v-if="activeQuestion.questionArray">
          <div class="tool-strengths__question mb-2">
            {{ activeQuestion.questionArray[0] }}
          </div>
          <div
            v-if="activeQuestion.questionArray.length === 2"
            class="tool-strengths__question__extension px-2">
            {{ activeQuestion.questionArray[1] }}
          </div>
        </template>
        <template v-else>
          <div class="tool-strengths__question mb-2">
            {{ activeQuestion.question }}
          </div>
        </template>
        <div v-if="mutableQuestions[activeIndex]"
             class="tool-strengths__answer">
          <textarea
            id="ts-answer-text-area"
            v-model="mutableQuestions[activeIndex].answer"
            class="tool-strengths__answer__input"
            :class="{ 'tool-strengths__answer__input--invalid': hasExceededWordLimit }"
            @keyup="checkWordCount">
          </textarea>
          <b-alert :show="isAnswerNullOrEmpty && hasAttempted"
                   variant="danger"
                   class="alert">
            This field is required
          </b-alert>
          <b-alert :show="hasExceededWordLimit"
                   variant="danger"
                   class="alert">
            {{ exceededWordLimitErrorMessage }}
          </b-alert>
        </div>
      </div>
    </template>

    <div class="tool-strengths__navigation my-4">
      <button
        class="cta cta--secondary"
        :class="{ 'd-none': ui.previous.hidden && activeIndex === 0 }"
        @click="onPrevious">
        <font-awesome-icon icon="chevron-left"
                           size="xs"></font-awesome-icon>
        {{ ui.previous.label }}
      </button>
      <button class="cta cta--primary--alt"
              @click="onNext">
        <template v-if="isLastQuestion">
          <font-awesome-icon :icon="['fal', 'save']"
                             size="1x"></font-awesome-icon>
          Save & Submit Survey
        </template>
        <template v-else>
          Next
          <font-awesome-icon icon="chevron-right"
                             size="xs"></font-awesome-icon>
        </template>
      </button>
    </div>
  </div>
</template>
<script>
import {
  listQuestions as listThreeSixtyQuestions,
  saveAnswers as saveThreeSixtyAnswers,
} from "~/Scripts/resources/threeSixtyResource";
import {
  listQuestions as listEngageQuestions,
  saveAnswers as saveEngageAnswers,
} from "~/Scripts/resources/engageResource";
import { saveAnswers as saveTeamStatementOrder } from "~/Scripts/resources/teamResource";
import { INSTANCE_TYPE, TEAM_FREE_QUESTION_TYPE } from "~/Components/constants";
import { utils } from "~/Scripts/vue/mixins/common";

export default {
  name: "ToolStrenghs",
  mixins: [utils],
  props: {
    id: {
      type: String,
      default: null,
    },
    asReviewer: {
      type: Boolean,
      default: false,
    },
    surveyQuestions: {
      type: Array,
      default: () => null,
      validator: val => {
        const [question] = val;
        console.log( question );

        const threeSixtyKeys = [
          "answer",
          "question",
          "maxAnswerLength",
          "questionId",
          "questionType",
          "questionTypeSystemCode",
        ];
        const engageKeys = [
          "question",
          "questionId",
          "answer",
          "questionType",
          "questionTypeSystemCode",
        ];
        const teamKeys = ["question", "answer", "type", "typeCode"];

        return (
          threeSixtyKeys.every( prop => prop in question )
          || engageKeys.every( prop => prop in question )
          || teamKeys.every( prop => prop in question )
        );
      },
    },
    next: {
      type: String,
      required: true,
    },
    instanceType: {
      type: String,
      required: true,
      validator: val => [
        INSTANCE_TYPE.THREE_SIXTY,
        INSTANCE_TYPE.PERSONA,
        INSTANCE_TYPE.STYLE,
        INSTANCE_TYPE.SURVEY,
        INSTANCE_TYPE.ENGAGE,
        INSTANCE_TYPE.TEAM,
      ].includes( val.toUpperCase() ),
    },
  },
  data: () => ( {
    instanceId: null,
    INSTANCE_TYPE,
    reviewerId: null,
    activeIndex: 0,
    questions: [],
    mutableQuestions: [],
    attempts: 0,
    ui: {
      previous: {
        label: "Back",
        hidden: ( window.history && window.history.length > 1 ) === false,
      },
      questions: {
        loading: false,
        error: false,
        errorMessage: "Failed to load questions",
      },
      answers: {
        save: false,
        error: false,
        errorMessage: "Failed to save answers",
      },
    },
    hasHistory: window.history.length > 1,
  } ),
  computed: {
    hasAttempted () {
      return this.attempts > 0;
    },
    isValid () {
      return this.isAnswerNullOrEmpty === false && this.hasExceededWordLimit === false;
    },
    isAnswerNullOrEmpty () {
      return this.activeQuestion.answer == null || this.activeQuestion.answer === "";
    },
    activeQuestion () {
      return this.mutableQuestions[this.activeIndex] || { question: null, answer: "" };
    },
    activeQuestionNumber () {
      return this.activeIndex + 1;
    },
    isLastQuestion () {
      return this.activeQuestionNumber === this.mutableQuestions.length;
    },
    hasExceededWordLimit () {
      if ( this.activeQuestion.maxAnswerLength <= 0 || this.activeQuestion.answer == null ) {
        return false;
      }

      return this.activeQuestion.answer.length >= this.activeQuestion.maxAnswerLength;
    },
    exceededWordLimitErrorMessage () {
      return `Max character limit of ${this.activeQuestion.maxAnswerLength}`;
    },
    saveAnswers () {
      if ( this.instanceType === this.INSTANCE_TYPE.THREE_SIXTY ) {
        return this.saveThreeSixtyAnswer;
      }
      if ( this.instanceType === this.INSTANCE_TYPE.ENGAGE ) {
        return this.saveEngageAnswer;
      }
      if ( this.instanceType === this.INSTANCE_TYPE.TEAM ) {
        return this.saveTeamAnswer;
      }
      return this.redirectToError;
    },
    loadQuestions () {
      if ( this.instanceType === this.INSTANCE_TYPE.THREE_SIXTY ) {
        return this.loadThreeSixtyQuestions;
      }
      if ( this.instanceType === this.INSTANCE_TYPE.ENGAGE ) {
        return this.loadThreeSixtyQuestions;
      }
      if ( this.instanceType === this.INSTANCE_TYPE.TEAM ) {
        return this.loadTeamQuestions;
      }
      return this.redirectToError;
    },
  },
  created () {
    const params = this.getURLParams();
    this.instanceId = this.id || params.id;
    // accomodate for 360, Engage and Teams
    this.reviewerId = params.reviewer || params.member;

    if ( this.asReviewer && this.reviewerId == null ) {
      console.error( "We have encountered an issue", this.reviewerId );
      return;
      // window.location.href = "/error";
    }

    // if the questions haven't been provided attempt to load them
    if ( this.surveyQuestions ) {
      this.mutableQuestions = this.surveyQuestions;
    } else if ( this.instanceId ) {
      this.loadQuestions();
    } else {
      this.redirectToError();
    }
  },
  methods: {
    onNext () {
      this.attempts += 1;

      if ( this.isValid ) {
        this.resetAttempts();

        if ( this.activeQuestionNumber === this.mutableQuestions.length ) {
          this.saveAnswers();
        } else {
          this.activeIndex += 1;
        }
      }
    },
    isNullOrEmpty ( val ) {
      return val == null || val === "";
    },
    onPrevious () {
      this.resetAttempts();

      if ( this.activeIndex === 0 ) {
        window.history.back();
      } else {
        this.activeIndex -= 1;
      }
    },
    checkWordCount () {
      if ( this.hasExceededWordLimit ) {
        const trimmed = this.activeQuestion.answer.slice( 0, this.activeQuestion.maxAnswerLength );
        this.mutableQuestions[this.activeIndex].answer = trimmed;
      }
    },
    saveThreeSixtyAnswer () {
      this.ui.answers.save = true;

      const mappedAnswers = this.mutableQuestions.map( q => ( {
        questionId: q.questionId,
        answer: q.answer,
      } ) );

      const payload = {
        instanceId: this.instanceId,
        answers: mappedAnswers,
        reviewerId: this.reviewerId,
      };

      saveThreeSixtyAnswers( payload )
        .then( response => {
          const { status, data } = response;

          if ( status === 200 && data ) {
            if ( this.asReviewer ) {
              this.$emit( "reviewer-save-success", this.reviewerId );
            } else {
              this.$emit( "save-success", data );
              window.location.href = `${this.next}?id=${this.instanceId}`;
            }
          } else {
            throw new Error( "Failed to save answers" );
          }
        } )
        .catch( ex => {
          console.error( ex );
          this.ui.answers.error = true;
        } )
        .finally( () => {
          this.ui.answers.save = false;
        } );
    },
    saveEngageAnswer () {
      this.ui.answers.save = true;

      const mappedAnswers = this.mutableQuestions.map( q => ( {
        questionId: q.questionId,
        answer: q.answer,
      } ) );

      const payload = {
        instanceId: this.instanceId,
        answers: mappedAnswers,
        reviewerId: this.reviewerId,
      };

      saveEngageAnswers( payload )
        .then( response => {
          const { status, data } = response;

          if ( status === 200 && data ) {
            if ( this.asReviewer ) {
              this.$emit( "reviewer-save-success", this.reviewerId );
            } else {
              this.$emit( "save-success", data );
              window.location.href = `${this.next}?id=${this.instanceId}`;
            }
          } else {
            throw new Error( "Failed to save answers" );
          }
        } )
        .catch( ex => {
          console.error( ex );
          this.ui.answers.error = true;
        } )
        .finally( () => {
          this.ui.answers.save = false;
        } );
    },
    saveTeamAnswer () {
      const error = new Error( "Failed to save statement order" );

      const s = this.mutableQuestions.find(
        q => q.typeCode === TEAM_FREE_QUESTION_TYPE.STRENGTH,
      ) || { answer: null };

      const w = this.mutableQuestions.find(
        q => q.typeCode === TEAM_FREE_QUESTION_TYPE.WEAKNESS,
      ) || { answer: null };

      const { answer: areasOfStrength } = s;
      const { answer: areasOfWeakness } = w;

      const payload = {
        instanceId: this.instanceId,
        statementRank: JSON.parse( null ),
        memberId: this.isNullOrEmpty( this.reviewerId ) ? JSON.parse( null ) : this.reviewerId,
        areasOfStrength,
        areasOfWeakness,
      };

      this.ui.answers.save = true;

      saveTeamStatementOrder( payload )
        .then( ( { status, data } ) => {
          if ( status === 200 && data ) {
            if ( this.asReviewer ) {
              this.$emit( "reviewer-save-success", this.reviewerId );
            } else {
              this.$emit( "save-success", data );
              window.location.href = `${this.next}?id=${this.instanceId}`;
            }
          } else {
            throw new Error( error );
          }
        } )
        .catch( ex => {
          console.error( ex );
          this.ui.answers.error = true;
        } )
        .finally( () => {
          this.ui.answers.save = false;
        } );
    },
    loadThreeSixtyQuestions () {
      this.ui.questions.loading = true;

      listThreeSixtyQuestions( this.instanceId, this.reviewerId )
        .then( response => {
          const { status, data } = response;

          if ( status === 200 && data ) {
            this.questions = data;

            this.mutableQuestions = [...this.questions]
              .map( q => {
                if ( q.questionTypeSystemCode === "FREE_TEXT" ) {
                  const questionArray = q.question.split( "<br>" );
                  const mutableQ = { ...q, questionArray };
                  return mutableQ;
                }

                return null;
              } )
              .filter( q2 => q2 != null );
          } else {
            throw new Error( "Failed to load questions" );
          }
        } )
        .catch( ex => {
          console.error( ex );
          this.ui.questions.error = true;
        } )
        .finally( () => {
          this.ui.questions.loading = false;
        } );
    },
    loadEngageQuestions () {
      this.ui.questions.loading = true;

      console.log( this.instamceId, this.reviewerId );

      listEngageQuestions( this.instanceId, this.reviewerId )
        .then( response => {
          const { status, data } = response;

          if ( status === 200 && data ) {
            this.questions = data;

            this.mutableQuestions = [...this.questions]
              .map( q => {
                if ( q.questionTypeSystemCode === "FREE_TEXT" ) {
                  const questionArray = q.question.split( "<br>" );
                  const mutableQ = { ...q, questionArray };
                  return mutableQ;
                }

                return null;
              } )
              .filter( q2 => q2 != null );

            // console.log( this.mutableQuestions );
          } else {
            throw new Error( "Failed to load questions" );
          }
        } )
        .catch( ex => {
          console.error( ex );
          this.ui.questions.error = true;
        } )
        .finally( () => {
          this.ui.questions.loading = false;
        } );
    },
    loadTeamQuestions () {
      /*
        Free text questions are provided by the Umbraco DB incase they aren't
        supplied these can be used as the default.
      */
      this.mutableQuestions = [
        {
          question: "What do you consider to be the three greatest strengths of this team?",
          type: TEAM_FREE_QUESTION_TYPE.STRENGTH,
          answer: null,
        },
        {
          question: "What do you think the team should focus on now to be even more effective?",
          type: TEAM_FREE_QUESTION_TYPE.WEAKNESS,
          answer: null,
        },
      ];
    },
    resetAttempts () {
      this.attempts = 0;
    },
    redirectToError () {
      window.location.href = "/error";
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../Styles/utils/index.less";

.tool-strengths {
  position: relative;
  border: 1px solid @blue;
  .border-radius(5px);
  padding: 16px;

  &__background {
    .opacity-background(@light-blue, 0.6);
  }

  &__navigation {
    display: flex;
    justify-content: space-between;
  }

  &__question {
    font-weight: 700;
    font-style: normal;
    font-size: 18px;
    color: @blue;

    &__extension {
      font-weight: 200;
      font-style: normal;
      font-size: 16px;

      &:after {
        content: " *";
        color: red;
      }
    }
  }

  &__answer {
    &__input {
      resize: none;
      border: 1px solid #333;
      width: 100%;
      height: 224px;
      padding: 4px 8px;

      &:focus {
        outline: none;
      }

      &--inavlid {
        border: 1px solid red;
      }
    }
  }
}
</style>
